import {AxiosInstance} from "axios";
import {LoyaltyCard, LoyaltyCardAnalysis} from "@/loyalty-card/interfaces/hds/LoyaltyCard";
import {LoyaltyCardMembershipItemStatistic, LoyaltyCardRewardItem} from "@/loyalty-card/interfaces/hds/LoyaltyCard";
import {LoyaltyCardBigBuyer, LoyaltyCardMigrationPayload} from "@/loyalty-card/interfaces/LoyaltyCardMigration";
import {MetaData} from "@/core/interfaces/api";
import apiClient from "@/core/http/clients/apiClient";
import {LoyaltyCardProduct, LoyaltyCardRewardProduct, LoyaltyCardRules} from "@/loyalty-card/interfaces/LoyaltyCard";
import {ProductSearchQueryParams} from "@/auto-complete-searchbar/interfaces/ISearchBarAutoComplete";

/**
 * Response of the loyalty card healthcenter analysis endpoint.
 *
 * The `analysis` block is the single source of truth typed by
 * {@link LoyaltyCardAnalysis} — its optional members drive the dashboard's
 * graceful degradation while the enriched HDS endpoint is rolled out.
 */
export interface LoyaltyCardAnalysisResponse extends LoyaltyCard {
    analysis: LoyaltyCardAnalysis;
}

export interface CreateLoyaltyCardAnimationParams {
    name: string;
    description_patient: string;
    coefficient: number;
    start_date: Date;
    end_date: Date;
    is_for_all_products: boolean;
    products?: number[];
}

/**
 * The loyalty card resource.
 * @author Tony Laurent <t.laurent@apodispharma.com>
 * @param {Object} client - The injected client.
 * @param {Object} hdsClient - The injected client.
 * @returns {Object} The public object.
 */
export default function (client: AxiosInstance, hdsClient: AxiosInstance) {
    return {
        /**
         * Get all loyalty cards.
         * @returns {Promise} Promise object represents the HTTP request.
         */
        get() {
            return client.get('/loyalty-cards', {
                params: {
                    include: 'organization.morphable,file,objectives.type,objectives.reward.type'
                }
            })
                .then(response => response.data.data)
                .catch(() => []);
        },

        /**
         * Read a loyalty card with its rules: tiers, points scale and the organization that set them.
         * Resolves null when the card cannot be read.
         */
        async read(id: number): Promise<LoyaltyCardRules | null> {
            return client.get(`/loyalty-cards/${id}`, {
                params: {
                    include: 'objectives.type,objectives.reward.type,point.reward.type,organization.morphable'
                }
            })
                .then(response => response.data.data)
                .catch(() => null);
        },

        async getLoyalcardStatistics(id: number): Promise<LoyaltyCardAnalysisResponse> {
            return hdsClient.get(`loyalty-cards/${id}/healthcenter/analysis`)
                .then(response => response.data.data)
                .catch(() => []);
        },

        /**
         * Get the loyalty card membership analysis.
         */
        async getMemberships(id: number, page = 1, limit = 10, fill?: string): Promise<{
            data: LoyaltyCardMembershipItemStatistic[];
            meta: MetaData
        }> {
            const params: Record<string, string | number> = {
                page,
                limit
            };

            if (fill && fill !== 'all') {
                params.fill = fill;
            }

            return hdsClient.get(`loyalty-cards/${id}/memberships/analysis`, {params})
                .then(response => response.data)
                .catch(() => []);
        },

        /**
         * Get the delivered rewards of a loyalty card.
         */
        async getRewards(id: number, page = 1, limit = 20): Promise<{
            data: LoyaltyCardRewardItem[];
            meta: MetaData
        }> {
            return hdsClient.get(`loyalty-cards/${id}/rewards/analysis`, {params: {page, limit}})
                .then(response => response.data)
                .catch(() => ({data: [], meta: {} as MetaData}));
        },

        /**
         * Export the cards and delivered rewards of a loyalty card (current
         * healthcenter) as a CSV file.
         */
        async exportAnalysis(id: number): Promise<Blob> {
            return hdsClient.get(`loyalty-cards/${id}/analysis/export`, {responseType: 'blob'})
                .then(response => response.data);
        },

        /**
         * Detect big buyers of the card range (current healthcenter) without a digital card.
         */
        async getBigBuyers(id: number): Promise<{ data: LoyaltyCardBigBuyer[] }> {
            return hdsClient.get(`loyalty-cards/${id}/big-buyers`)
                .then(response => response.data)
                .catch(() => ({data: []}));
        },

        /**
         * Create a pre-filled card for a customer from the selected historical purchases.
         */
        async createPrefilledCard(
            customerId: number,
            loyaltyCardId: number,
            purchases: LoyaltyCardMigrationPayload['purchases']
        ) {
            return hdsClient.post(`customers/${customerId}/loyalty-cards/${loyaltyCardId}/migration`, {
                data: {purchases}
            })
                .then(response => response.data);
        },

        /**
         * List animation belonging to a loyalty card that were created by the provided healthcenter.
         *
         * @param {number} healthcenterId
         * @param {number} loyaltyCardId
         *
         * @return {Promise}
         */
        animations(healthcenterId: number, loyaltyCardId: number) {
            return client.get(`/healthcenters/${healthcenterId}/loyalty-cards/${loyaltyCardId}/animations`)
                .then(response => response.data.data);
        },

        /**
         * List animation belonging to a loyalty card that were created by the provided healthcenter.
         *
         * @param {number} healthcenterId
         * @param {number} loyaltyCardId
         * @param {CreateLoyaltyCardAnimationParams} params
         *
         *  @return {Promise}
         */
        createAnimation(healthcenterId: number, loyaltyCardId: number, params: CreateLoyaltyCardAnimationParams) {
            return client.post(`/healthcenters/${healthcenterId}/loyalty-cards/${loyaltyCardId}/animations`, params)
                .then(response => response.data.data);
        },

        /**
         * Delete an animation.
         */
        deleteAnimation(healthcenterId: number, loyaltyCardId: number, animationId: number): Promise<{deleted: boolean}> {
            return client.delete(`/healthcenters/${healthcenterId}/loyalty-cards/${loyaltyCardId}/animations/${animationId}`);
        },

        /**
         * List animation transactions.
         *
         * @param {number} animationId
         *
         * @return {Promise}
         */
        animationTransactions(animationId: number) {
            return hdsClient.get(`loyalty-card-animations/${animationId}/transactions`)
                .then(response => response.data.data);
        },

        /**
         * Get the targeting products of a loyalty card.
         *
         * @param loyaltyCardId
         * @param params
         */
        async products(loyaltyCardId: number | string, params: {
            size?: number;
            page?: number;
            search?: string;
            sort?: {
                key: string,
                order: 'asc' | 'desc' | undefined
            },
            type?: 'all' | 'included' | 'excluded' | 'eligible';
        } & ProductSearchQueryParams): Promise<{ data: LoyaltyCardProduct[]; meta: { total: number; page: number; size: number } }> {
            return apiClient.get(`loyalty-cards/${loyaltyCardId}/products`, {params})
                .then(response => response.data)
                .catch(() => []);
        },

        /**
         * Get the products a loyalty card offers as a reward, a page at a time.
         *
         * @param loyaltyCardId
         * @param params
         */
        async rewardProducts(loyaltyCardId: number | string, params: {
            size?: number;
            page?: number;
        }): Promise<{ data: LoyaltyCardRewardProduct[]; meta: MetaData }> {
            return apiClient.get(`loyalty-cards/${loyaltyCardId}/reward-products`, {params})
                .then(response => response.data)
                .catch(() => ({data: [], meta: {total: 0} as MetaData}));
        },

        /**
         * Exclude products from a loyalty card for the current healthcenter.
         *
         * @param loyaltyCardId
         * @param productIds
         */
        async excludeProducts(loyaltyCardId: number | string, productIds: number[]) {
            return apiClient.post(`loyalty-cards/${loyaltyCardId}/products/exclude`, {product_ids: productIds})
                .then(response => response.data);
        },

        /**
         * Include products from a loyalty card for the current healthcenter.
         *
         * @param loyaltyCardId
         * @param productIds
         */
        async includeProducts(loyaltyCardId: number | string, productIds: number[]) {
            return apiClient.post(`loyalty-cards/${loyaltyCardId}/products/include`, {product_ids: productIds})
                .then(response => response.data);
        },

        /**
         * Delete healthcenter product.
         *
         * @param loyaltyCardId
         * @param healthcenterProductId
         */
        async deleteHealthcenterProduct(loyaltyCardId: number | string, healthcenterProductId: number) {
            return apiClient.delete(`loyalty-cards/${loyaltyCardId}/healthcenter-products/${healthcenterProductId}`)
                .then(response => response.data);
        }
    };
}
